// React
import React from "react";

// MUI
import { LockOutlined, PushPinOutlined } from "@mui/icons-material";

// Third party imports
import { useTranslation } from "react-i18next";

import {
  BlockFrame,
  BlockLink,
  COMPANY_STATUSES,
  EmptyRows,
  GroupedView,
  itemsOf,
  joinMeta,
  Row,
  RowList,
  StatTile,
  statusColor,
  StatusPill,
  Tag,
  TypeTag,
  useFormatters,
  useFrame,
  useStatusLabel,
} from "./BlockParts";
import { groupCount, NONE_KEY } from "./blockUtils";

// Blocks of clients, notes, notifications, operators and payments.
// Props of every block: { data, view, variant, onNavigate }.

const linkButton =
  "inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-[12px] font-medium text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900";

export function CompaniesBlock({ data, view, variant, onNavigate }) {
  const { t } = useTranslation("aiAssistant");
  const format = useFormatters();
  const statusLabel = useStatusLabel();
  const items = itemsOf(data);
  const title = t("blocks.titles.search_companies");
  const frame = useFrame(data, items, title, view);
  const list = (
    <RowList
      items={items}
      variant={variant}
      renderRow={(company) => (
        <Row
          variant={variant}
          title={company.name}
          href={company.url}
          onNavigate={onNavigate}
          meta={joinMeta(
            company.managing_team,
            company.city,
            company.contract_renew_date
              ? t("blocks.company.renewsOn", { date: format.day(company.contract_renew_date) })
              : null
          )}
          aside={
            <>
              <StatusPill status={company.status} />
              <TypeTag type={company.type} />
            </>
          }
        />
      )}
    />
  );
  if (view === "list") return <BlockFrame {...frame}>{list}</BlockFrame>;

  const byStatus = view === "by_status";
  const groups = groupCount(
    items,
    byStatus ? (item) => item?.status : (item) => item?.managing_team,
    byStatus ? COMPANY_STATUSES : undefined
  );
  return (
    <BlockFrame {...frame}>
      <GroupedView
        groups={groups}
        labelOf={(key) =>
          key === NONE_KEY
            ? t(`blocks.none.${view}`, { defaultValue: t("blocks.none.other") })
            : byStatus
              ? statusLabel(key)
              : key
        }
        colorOf={byStatus ? statusColor : undefined}
        total={items.length}
        count={frame.count}
        variant={variant}
        label={`${title} · ${t(`blocks.views.${view}`)}`}
        list={list}
      />
    </BlockFrame>
  );
}

export function CompanyCardBlock({ data, variant, onNavigate }) {
  const { t } = useTranslation("aiAssistant");
  const format = useFormatters();
  if (!data?.name) return null;
  const place = [data.city, data.province, data.region]
    .filter(Boolean)
    .filter((value, index, all) => all.indexOf(value) === index)
    .join(", ");
  const details = [
    [t("blocks.company.team"), data.managing_team],
    [t("blocks.company.place"), place],
    [t("blocks.company.contractStart"), format.day(data.contract_start_date)],
    [t("blocks.company.contractRenew"), format.day(data.contract_renew_date)],
  ].filter(([, value]) => value);
  const pages = data.pages && typeof data.pages === "object" ? data.pages : {};
  const pageLinks = ["board", "information", "subtasks", "notes"].filter((key) => pages[key]);
  const operators = Array.isArray(data.team_operators) ? data.team_operators : [];
  const hasValue =
    data.contract_value !== undefined &&
    data.contract_value !== null &&
    format.money(data.contract_value);

  return (
    <div className="surface-card overflow-hidden">
      <div className="flex items-start gap-3 border-b border-gray-100 px-3 py-3">
        <div className="min-w-0 flex-1">
          <BlockLink
            href={data.url}
            onNavigate={onNavigate}
            className="block text-[15px] leading-snug font-semibold text-gray-900"
          >
            {data.name}
          </BlockLink>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <StatusPill status={data.status} />
            <TypeTag type={data.type} />
            {data.stop_renew && <Tag tone="danger">{t("blocks.company.stopRenew")}</Tag>}
            {data.is_active === false && <Tag>{t("blocks.inactive")}</Tag>}
          </div>
        </div>
      </div>

      <div
        className={`grid gap-2 px-3 pt-3 ${hasValue ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"}`}
      >
        <StatTile
          label={t("blocks.company.openTasks")}
          value={format.number(data.open_tasks_count) || "0"}
        />
        <StatTile
          label={t("blocks.company.openSubtasks")}
          value={format.number(data.open_subtasks_count) || "0"}
        />
        <StatTile
          label={t("blocks.company.services")}
          value={format.number(data.services_count) || "0"}
        />
        {hasValue && (
          <StatTile
            label={t("blocks.company.contractValue")}
            value={format.money(data.contract_value)}
          />
        )}
      </div>

      {details.length > 0 && (
        <dl className="grid grid-cols-1 gap-x-4 gap-y-1.5 px-3 pt-3 text-[13px] sm:grid-cols-2">
          {details.map(([label, value]) => (
            <div key={label} className="flex min-w-0 gap-2">
              <dt className="shrink-0 text-gray-500">{label}</dt>
              <dd className="min-w-0 truncate text-gray-900">{value}</dd>
            </div>
          ))}
        </dl>
      )}

      {data.ai_status_report && (
        <div className="px-3 pt-3">
          <p className="section-title mb-1">{t("blocks.company.statusReport")}</p>
          <p
            className={`text-[13px] leading-relaxed whitespace-pre-line text-gray-700 ${variant === "page" ? "" : "line-clamp-4"}`}
          >
            {data.ai_status_report}
          </p>
        </div>
      )}

      {operators.length > 0 && (
        <div className="px-3 pt-3">
          <p className="section-title mb-1.5">{t("blocks.company.teamOperators")}</p>
          <div className="flex flex-wrap gap-1.5">
            {operators.map((operator, index) => (
              <BlockLink
                key={operator.url || `${operator.name}-${index}`}
                href={operator.url}
                onNavigate={onNavigate}
                className="rounded-full bg-gray-100 px-2 py-0.5 text-[12px] text-gray-700 transition-colors hover:bg-gray-200 hover:text-gray-900"
                bare
              >
                {operator.name}
              </BlockLink>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 px-3 pt-3 pb-3">
        {pageLinks.map((key) => (
          <BlockLink
            key={key}
            href={pages[key]}
            onNavigate={onNavigate}
            className={linkButton}
            bare
          >
            {t(`blocks.company.pages.${key}`)}
          </BlockLink>
        ))}
      </div>
    </div>
  );
}

export function NotesBlock({ data, variant, onNavigate }) {
  const { t } = useTranslation("aiAssistant");
  const format = useFormatters();
  const items = itemsOf(data);
  const title = data?.company
    ? t("blocks.titles.list_company_notes", { company: data.company })
    : t("blocks.titles.notes");
  const frame = useFrame(data, items, title, "list");
  return (
    <BlockFrame
      {...frame}
      action={
        data?.url ? (
          <BlockLink
            href={data.url}
            onNavigate={onNavigate}
            className="text-[12px] font-medium text-primary-strong"
          >
            {t("blocks.openNotes")}
          </BlockLink>
        ) : null
      }
    >
      <RowList
        items={items}
        variant={variant}
        renderRow={(note) => (
          <div className="min-w-0">
            <p className="flex min-w-0 items-center gap-1.5 text-[14px] font-medium text-gray-900">
              {note.pinned && (
                <PushPinOutlined
                  sx={{ fontSize: 14 }}
                  className="shrink-0 text-primary-strong"
                  titleAccess={t("blocks.notes.pinned")}
                />
              )}
              {note.is_private && (
                <LockOutlined
                  sx={{ fontSize: 14 }}
                  className="shrink-0 text-gray-500"
                  titleAccess={t("blocks.notes.private")}
                />
              )}
              <span className={variant === "page" ? "" : "truncate"}>
                {note.title || t("blocks.notes.untitled")}
              </span>
            </p>
            {note.text && (
              <p
                className={`mt-0.5 text-[13px] leading-relaxed whitespace-pre-line text-gray-600 ${variant === "page" ? "line-clamp-6" : "line-clamp-2"}`}
              >
                {note.text}
              </p>
            )}
            <p className="mt-1 text-[12px] text-gray-500">
              {joinMeta(note.author, format.day(note.created_at))}
            </p>
          </div>
        )}
      />
    </BlockFrame>
  );
}

export function NotificationsBlock({ data, variant }) {
  const { t } = useTranslation("aiAssistant");
  const format = useFormatters();
  const items = itemsOf(data);
  const frame = useFrame(data, items, t("blocks.titles.my_notifications"), "list");
  const unread = typeof data?.unread_count === "number" ? data.unread_count : null;
  return (
    <BlockFrame
      {...frame}
      subtitle={unread !== null ? t("blocks.unread", { count: unread }) : undefined}
    >
      <RowList
        items={items}
        variant={variant}
        renderRow={(notification) => (
          <div className="flex items-start gap-2.5">
            <span
              className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${notification.is_read ? "bg-transparent" : "bg-primary"}`}
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <p
                className={`text-[13px] leading-snug text-gray-900 ${variant === "page" ? "" : "line-clamp-2"}`}
              >
                {!notification.is_read && (
                  <span className="sr-only">{t("blocks.unreadMark")}: </span>
                )}
                {notification.text}
              </p>
              <p className="mt-0.5 text-[12px] text-gray-500">
                {joinMeta(notification.from, format.moment(notification.created_at))}
              </p>
            </div>
          </div>
        )}
      />
    </BlockFrame>
  );
}

export function OperatorsBlock({ data, view, variant, onNavigate }) {
  const { t } = useTranslation("aiAssistant");
  const items = itemsOf(data);
  const title = t("blocks.titles.list_operators");
  const frame = useFrame(data, items, title, view);
  const list = (
    <RowList
      items={items}
      variant={variant}
      renderRow={(operator) => (
        <Row
          variant={variant}
          title={operator.name}
          href={operator.url}
          onNavigate={onNavigate}
          meta={Array.isArray(operator.teams) ? operator.teams.join(", ") : null}
          aside={operator.is_active === false ? <Tag>{t("blocks.inactive")}</Tag> : null}
        />
      )}
    />
  );
  if (view !== "by_team") return <BlockFrame {...frame}>{list}</BlockFrame>;
  return (
    <BlockFrame {...frame}>
      <GroupedView
        groups={groupCount(items, (item) => item?.teams)}
        labelOf={(key) => (key === NONE_KEY ? t("blocks.none.by_team") : key)}
        total={items.length}
        count={frame.count}
        // An operator in two teams counts in both
        multi
        variant={variant}
        label={`${title} · ${t("blocks.views.by_team")}`}
        list={list}
      />
    </BlockFrame>
  );
}

export function PaymentsSummaryBlock({ data, variant, onNavigate }) {
  const { t } = useTranslation("aiAssistant");
  const format = useFormatters();
  const payments = Array.isArray(data?.latest_payments) ? data.latest_payments : [];
  const unsolved = Number.parseFloat(String(data?.unsolved_total ?? ""));
  const title = data?.company
    ? t("blocks.titles.company_payments_summary", { company: data.company })
    : t("blocks.titles.payments");
  return (
    <BlockFrame
      title={title}
      action={
        data?.url ? (
          <BlockLink
            href={data.url}
            onNavigate={onNavigate}
            className="text-[12px] font-medium text-primary-strong"
          >
            {t("blocks.openPayments")}
          </BlockLink>
        ) : null
      }
    >
      <div className="grid grid-cols-2 gap-2 px-3 pt-3">
        <StatTile
          label={t("blocks.payments.count")}
          value={format.number(data?.payments_count) || "0"}
        />
        <StatTile
          label={t("blocks.payments.unsolved")}
          value={format.money(data?.unsolved_total) || format.money(0)}
          tone={unsolved > 0 ? "danger" : undefined}
        />
      </div>
      <p className="section-title px-3 pt-3 pb-1">{t("blocks.payments.latest")}</p>
      {payments.length ? (
        <RowList
          items={payments}
          variant={variant}
          getKey={(payment, index) => `${payment.date}-${index}`}
          renderRow={(payment) => (
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-medium text-gray-900">
                  {format.day(payment.date) || "—"}
                </p>
                <p className="mt-0.5 truncate text-[12px] text-gray-500">
                  {joinMeta(payment.type, payment.status)}
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <span className="text-[13px] font-semibold text-gray-900 tabular-nums">
                  {format.money(payment.amount)}
                </span>
                {payment.is_recovered && <Tag tone="primary">{t("blocks.payments.recovered")}</Tag>}
              </div>
            </div>
          )}
        />
      ) : (
        <EmptyRows />
      )}
    </BlockFrame>
  );
}
